import { type SeriesSummary } from "@crowdcoursing/api/openapi";
import { Add as AddIcon, ArrowDropDown as ArrowDropDownIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";
import {
  Date,
  FIELD_NAME_DATE,
  FIELD_NAME_TIME_RANGE,
  Form,
  type FormProps,
  ResetButton,
  SubmitButton,
  type TextFieldValue,
  TimeRange,
} from "../../atoms/Form";
import { CapacityCounter } from "../../molecules/CapacityCounter/CapacityCounter";
import { DayOfWeekSelector } from "../../molecules/DayOfWeekSelector/DayOfWeekSelector";
import { MeetingTimeSummary } from "../../molecules/MeetingTimeSummary/MeetingTimeSummary";
import {
  useAutoAddMode,
  useMeetingTimeOperations,
  useMeetingTimeSlots,
  useSlotExpansion,
} from "./AddEventTimesModal.hooks";
import { type AddEventTimesModalProps, type FormValues } from "./AddEventTimesModal.types";
import {
  extractMeetingTimesFromForm,
  generateFieldName,
  isMeetingTimeComplete,
  STRINGS,
} from "./AddEventTimesModal.utils";

interface AddEventTimesDialogProps extends Omit<AddEventTimesModalProps, "disabled" | "eventName"> {
  open: boolean;
  onClose: () => void;
  series?: SeriesSummary[];
}

export const AddEventTimesDialog = ({
  onSubmit,
  eventId,
  defaultDate,
  open,
  onClose,
  series,
}: AddEventTimesDialogProps) => {
  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set([1, 2, 3, 4, 5])); // Default to weekdays
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<{
    change: (field: string, value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | null) => void;
  }>();

  // Use extracted hooks
  const slotsHook = useMeetingTimeSlots();
  const { meetingTimeSlots, removeMeetingTimeSlot, resetSlots } = slotsHook;

  const expansionHook = useSlotExpansion();
  const {
    expandedSlots,
    toggleSlotExpansion,
    handleFieldFocus,
    handleFieldBlur,
    resetExpansion,
    cleanupSlotExpansion,
  } = expansionHook;

  const { autoAddMode, autoAddEndDate, startAutoAddMode, cancelAutoAddMode, setEndDate, resetAutoAddMode } =
    useAutoAddMode();

  // When the modal opens and we have a default date, set it for the first slot
  useEffect(() => {
    if (open && meetingTimeSlots.length === 1 && defaultDate && formRef.current) {
      const firstSlotId = meetingTimeSlots[0].id;
      formRef.current.change(generateFieldName(firstSlotId, FIELD_NAME_DATE), defaultDate);
    }
  }, [open, defaultDate, meetingTimeSlots]);

  // Scroll to bottom of the modal to show new meeting time
  const scrollToElement = useCallback((slotId: string) => {
    if (scrollContainerRef.current) {
      const element = document.getElementById(`meeting-time-${slotId}`);
      if (element) {
        const scrollContainer = scrollContainerRef.current.parentElement;
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const elementBottom = elementRect.bottom + 32; // Add more padding for better visibility
          const containerBottom = containerRect.bottom;

          if (elementBottom > containerBottom) {
            const scrollNeeded = elementBottom - containerBottom;
            scrollContainer.scrollBy({
              behavior: "smooth",
              top: scrollNeeded,
            });
          }
        }
      }
    }
  }, []);

  // Initialize meeting time operations with dependencies
  const { addMeetingTime, autoAddMeetingTimes } = useMeetingTimeOperations(
    meetingTimeSlots,
    selectedDays,
    slotsHook,
    expansionHook,
    scrollToElement,
  );

  const closeDialog = useCallback(() => {
    onClose();
    resetSlots();
    resetExpansion();
    setSelectedDays(new Set([1, 2, 3, 4, 5])); // Reset to weekdays
    setAddMenuAnchor(null);
    resetAutoAddMode();
  }, [onClose, resetSlots, resetExpansion, resetAutoAddMode]);

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues) => {
      const meetingTimes = extractMeetingTimesFromForm(formValues, meetingTimeSlots);

      if (meetingTimes.length > 0) {
        onSubmit(meetingTimes, eventId);
        closeDialog();
      }
    },
    [meetingTimeSlots, onSubmit, closeDialog, eventId],
  );

  const modalTitle = "Create Event Times";

  const handleAddMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  const handleRegularAdd = (
    formValues: FormValues,
    formApi: {
      change: (field: string, value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | null) => void;
    },
  ) => {
    const newSlotId = addMeetingTime(formValues, formApi);
    if (defaultDate && newSlotId) {
      formApi.change(generateFieldName(newSlotId, FIELD_NAME_DATE), defaultDate);
    }
    handleAddMenuClose();
  };

  const handleAutoAdd = () => {
    startAutoAddMode();
    handleAddMenuClose();
  };

  const handleAutoAddCancel = () => {
    cancelAutoAddMode();
  };

  const handleAutoAddConfirm = (
    formValues: FormValues,
    formApi: {
      change: (field: string, value: DateTime | [DateTime | null, DateTime | null] | TextFieldValue | null) => void;
    },
  ) => {
    if (autoAddEndDate) {
      autoAddMeetingTimes(formValues, formApi, autoAddEndDate);
      // Reset the auto-add mode after successful generation
      cancelAutoAddMode();
    }
  };

  const removeMeetingTime = useCallback(
    (idToRemove: string) => {
      removeMeetingTimeSlot(idToRemove);
      cleanupSlotExpansion(idToRemove);
    },
    [removeMeetingTimeSlot, cleanupSlotExpansion],
  );

  return (
    <Form<FormValues>
      onSubmit={(values, form, callback) => {
        return handleSubmit(values, form, callback);
      }}
    >
      {({ values, form }) => {
        formRef.current = form;
        return (
          <Dialog
            actions={
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  height: "calc(100vh - 200px)", // Leave space for modal header and padding
                  maxHeight: "800px",
                  minHeight: "400px",
                }}
              >
                {/* Content area with fixed header and footer */}
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {/* Fixed header section */}
                  <Box sx={{ borderBottom: 1, borderColor: "divider", p: 2 }}>
                    <Stack
                      direction={{ sm: "row", xs: "column" }}
                      spacing={2}
                      alignItems={{ sm: "center", xs: "stretch" }}
                      justifyContent="space-between"
                    >
                      <DayOfWeekSelector selectedDays={selectedDays} onDaysChange={setSelectedDays} />

                      {/* Auto-add mode */}
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Add multiple event times
                        </Typography>
                        {autoAddMode ? (
                          <Stack spacing={1} direction={{ sm: "row", xs: "column" }} alignItems="flex-start">
                            <TextField
                              label="End Date"
                              type="date"
                              size="small"
                              fullWidth
                              value={autoAddEndDate?.toFormat ? autoAddEndDate.toFormat("yyyy-MM-dd") : ""}
                              onChange={(e) => {
                                const dateString = e.target.value;
                                if (dateString) {
                                  setEndDate(DateTime.fromISO(dateString));
                                } else {
                                  setEndDate(null);
                                }
                              }}
                              slotProps={{
                                htmlInput: {
                                  min: DateTime.now().toFormat("yyyy-MM-dd"), // Disable past dates
                                },
                                inputLabel: {
                                  shrink: true,
                                },
                              }}
                            />
                            <Stack direction="row" spacing={1} sx={{ width: { sm: "auto", xs: "100%" } }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleAutoAddConfirm(values, form)}
                                disabled={!autoAddEndDate || selectedDays.size === 0}
                                fullWidth
                              >
                                Generate
                              </Button>
                              <Button variant="outlined" size="small" onClick={handleAutoAddCancel} fullWidth>
                                Cancel
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Stack
                            direction={{ sm: "row", xs: "column" }}
                            spacing={1}
                            sx={{ width: { sm: "auto", xs: "100%" } }}
                          >
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => handleRegularAdd(values, form)}
                              disabled={selectedDays.size === 0}
                              sx={{ width: { sm: "auto", xs: "100%" } }}
                            >
                              {STRINGS.tAddAnotherTime}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={handleAddMenuClick}
                              disabled={selectedDays.size === 0}
                              sx={{
                                minWidth: "40px",
                                px: 1,
                                width: { sm: "auto", xs: "100%" },
                              }}
                            >
                              <ArrowDropDownIcon />
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Box>

                  {/* Add Menu */}
                  <Menu
                    anchorEl={addMenuAnchor}
                    open={Boolean(addMenuAnchor)}
                    onClose={handleAddMenuClose}
                    anchorOrigin={{
                      horizontal: "right",
                      vertical: "bottom",
                    }}
                    transformOrigin={{
                      horizontal: "right",
                      vertical: "top",
                    }}
                  >
                    <MenuItem onClick={() => handleRegularAdd(values, form)}>Add Single Time</MenuItem>
                    <MenuItem onClick={handleAutoAdd}>Auto-Add Until Date</MenuItem>
                  </Menu>

                  {/* Scrollable content area */}
                  <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                    <Stack spacing={2} ref={scrollContainerRef}>
                      {meetingTimeSlots.map((slot) => (
                        <Paper
                          key={slot.id}
                          id={`meeting-time-${slot.id}`}
                          sx={{ border: "1px solid", borderColor: "divider", p: 2 }}
                        >
                          <Box sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {STRINGS.tMeetingTime} {slot.displayIndex}
                            </Typography>
                            {meetingTimeSlots.length > 1 && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeMeetingTime(slot.id)}
                                aria-label={STRINGS.tRemoveTime}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>

                          <Stack spacing={2}>
                            <MeetingTimeSummary
                              dateFieldName={generateFieldName(slot.id, FIELD_NAME_DATE)}
                              timeRangeFieldName={generateFieldName(slot.id, FIELD_NAME_TIME_RANGE)}
                              capacityFieldName={generateFieldName(slot.id, "capacity")}
                              slotId={slot.id}
                              isExpanded={expandedSlots.has(slot.id)}
                              onToggleExpansion={(slotId) => toggleSlotExpansion(slotId, scrollToElement)}
                              onFieldFocus={handleFieldFocus}
                              onFieldBlur={handleFieldBlur}
                              formValues={values}
                            />

                            <Collapse in={expandedSlots.has(slot.id)} timeout="auto">
                              <Stack spacing={2} sx={{ mt: 1 }}>
                                <Date
                                  name={generateFieldName(slot.id, FIELD_NAME_DATE)}
                                  label={STRINGS.tDate}
                                  datePickerProps={{
                                    disablePast: true,
                                  }}
                                  required
                                />

                                <TimeRange
                                  name={generateFieldName(slot.id, FIELD_NAME_TIME_RANGE)}
                                  label={STRINGS.tTimeRange}
                                  required
                                />

                                {series && series.length > 0 && (
                                  <FormControl fullWidth>
                                    <InputLabel id={`series-select-${slot.id}-label`}>{STRINGS.tSeries}</InputLabel>
                                    <Select
                                      labelId={`series-select-${slot.id}-label`}
                                      id={`series-select-${slot.id}`}
                                      name={generateFieldName(slot.id, "seriesId")}
                                      value={values[generateFieldName(slot.id, "seriesId")] || ""}
                                      label={STRINGS.tSeries}
                                      onChange={(e) =>
                                        form.change(generateFieldName(slot.id, "seriesId"), e.target.value)
                                      }
                                    >
                                      <MenuItem value="">
                                        <em>None</em>
                                      </MenuItem>
                                      {series.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>
                                          {s.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}

                                <CapacityCounter
                                  name={generateFieldName(slot.id, "capacity")}
                                  label={STRINGS.tCapacity}
                                  required
                                />
                              </Stack>
                            </Collapse>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>

                  {/* Fixed footer with action buttons */}
                  <Box
                    sx={{
                      bgcolor: "background.paper",
                      borderColor: "divider",
                      borderTop: 1,
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      p: 2, // Ensure it's opaque
                    }}
                  >
                    <ResetButton
                      onClick={() => {
                        // Clear all field values for all slots before resetting
                        meetingTimeSlots.forEach((slot) => {
                          form.change(generateFieldName(slot.id, FIELD_NAME_DATE), null);
                          form.change(generateFieldName(slot.id, FIELD_NAME_TIME_RANGE), null);
                          form.change(generateFieldName(slot.id, "capacity"), null);
                          form.change(generateFieldName(slot.id, "seriesId"), null);
                        });
                        resetSlots();
                        resetExpansion();
                        setSelectedDays(new Set([1, 2, 3, 4, 5]));
                        resetAutoAddMode();
                        form.reset();
                      }}
                    >
                      {STRINGS.tReset}
                    </ResetButton>
                    <SubmitButton disabled={!meetingTimeSlots.every((slot) => isMeetingTimeComplete(slot.id, values))}>
                      {STRINGS.tCreate}
                    </SubmitButton>
                  </Box>
                </Box>
              </Box>
            }
            dialogType="custom"
            maxWidth="md"
            onDialogClose={closeDialog}
            open={open}
            title={modalTitle}
            titleSx={{
              background: (theme: { palette: { primary: { main: string; dark: string } } }) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              p: 2,
            }}
          />
        );
      }}
    </Form>
  );
};
